import type { Dapp } from '@src/dto/Dapp';
import { isRight } from 'fp-ts/Either';
import {
  createElement,
  createContext,
  use,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AuthStateType, useAuth } from '../../../hooks/useAuth';
import { usePublisher } from '../../../hooks/usePublisher';
import { useTRPC } from '../../../hooks/useTRPC';
import type { KybStatusData } from '../../../lib/verificationStatus';
import { captureAndReportError } from '../../../utils/errorReporting';

interface SidebarData {
  publisherUserJoinId: string | null;
  kybStatusData: KybStatusData | null;
  hasKybStatusData: boolean;
  dapps: Dapp[];
}

interface SidebarDataContextValue {
  data: SidebarData;
  isLoading: boolean;
  refetchDapps: () => void;
  removeDapp: (dappId: string) => void;
}

interface SidebarDappRequest {
  publisherRequestId: number;
  dappRequestId: number;
}

const INITIAL_DATA: SidebarData = {
  publisherUserJoinId: null,
  kybStatusData: null,
  hasKybStatusData: false,
  dapps: [],
};

const SidebarDataContext = createContext<SidebarDataContextValue | null>(null);

export const SidebarDataProvider = ({ children }: { children: ReactNode }) => {
  const [authState] = useAuth();
  const { activePublisher } = usePublisher();
  const trpc = useTRPC();
  const [data, setData] = useState<SidebarData>(INITIAL_DATA);
  const [isLoading, setIsLoading] = useState(true);
  const publisherRequestIdRef = useRef(0);
  const dappRequestIdRef = useRef(0);
  const activePublisherUserJoinId =
    activePublisher?.publisherUserJoinId ?? null;
  const hasPublisherProfile = Boolean(activePublisher);
  const onboardingState =
    authState.type === AuthStateType.Authenticated
      ? authState.user.onboardingState
      : null;

  const updateDataIfCurrent = (
    publisherRequestId: number,
    updater: (previousData: SidebarData) => SidebarData,
  ) => {
    if (publisherRequestIdRef.current === publisherRequestId) {
      setData(updater);
    }
  };

  const updateDappDataIfCurrent = (
    request: SidebarDappRequest,
    updater: (previousData: SidebarData) => SidebarData,
  ) => {
    if (
      publisherRequestIdRef.current === request.publisherRequestId &&
      dappRequestIdRef.current === request.dappRequestId
    ) {
      setData(updater);
    }
  };

  const fetchDapps = (request: SidebarDappRequest) => {
    const promises: Promise<void>[] = [];

    if (hasPublisherProfile) {
      promises.push(
        trpc.listDapps
          .query(undefined)
          .then((result) => {
            if ('_tag' in result && isRight(result)) {
              updateDappDataIfCurrent(request, (previousData) => ({
                ...previousData,
                dapps: result.right,
              }));
            }
          })
          .catch((error) => {
            captureAndReportError('Failed to fetch dapps:', error);
          }),
      );
    } else {
      updateDappDataIfCurrent(request, (previousData) => ({
        ...previousData,
        dapps: [],
      }));
    }

    return Promise.all(promises);
  };

  const refetchDapps = () => {
    if (
      authState.type !== AuthStateType.Authenticated ||
      authState.user.onboardingState === 'EmailVerification' ||
      authState.user.onboardingState === 'ProfileCreation'
    ) {
      return;
    }

    const request = {
      publisherRequestId: publisherRequestIdRef.current,
      dappRequestId: dappRequestIdRef.current + 1,
    };
    dappRequestIdRef.current = request.dappRequestId;

    void fetchDapps(request);
  };

  const removeDapp = (dappId: string) => {
    setData((previousData) => ({
      ...previousData,
      dapps: previousData.dapps.filter((dapp) => dapp.id !== dappId),
    }));
  };

  useEffect(() => {
    const publisherRequestId = publisherRequestIdRef.current + 1;
    publisherRequestIdRef.current = publisherRequestId;
    const bootstrapDappRequest = {
      publisherRequestId,
      dappRequestId: dappRequestIdRef.current + 1,
    };
    dappRequestIdRef.current = bootstrapDappRequest.dappRequestId;
    setData({
      ...INITIAL_DATA,
      publisherUserJoinId: activePublisherUserJoinId,
    });

    if (
      authState.type !== AuthStateType.Authenticated ||
      authState.user.onboardingState === 'EmailVerification' ||
      authState.user.onboardingState === 'ProfileCreation'
    ) {
      setIsLoading(false);
      return () => {
        publisherRequestIdRef.current += 1;
      };
    }

    setIsLoading(true);

    const timer = window.setTimeout(async () => {
      const promises: Promise<unknown>[] = [fetchDapps(bootstrapDappRequest)];

      if (hasPublisherProfile) {
        promises.push(
          trpc.getKybStatus
            .query(undefined)
            .then((result) => {
              if ('_tag' in result && isRight(result)) {
                updateDataIfCurrent(publisherRequestId, (previousData) => ({
                  ...previousData,
                  kybStatusData: result.right,
                  hasKybStatusData: true,
                }));
              }
            })
            .catch((error) => {
              captureAndReportError('Failed to fetch KYB status:', error);
            }),
        );
      }

      await Promise.all(promises);

      if (publisherRequestIdRef.current === publisherRequestId) {
        setIsLoading(false);
      }
    }, 100);

    return () => {
      publisherRequestIdRef.current += 1;
      window.clearTimeout(timer);
    };
  }, [
    activePublisherUserJoinId,
    authState.type,
    hasPublisherProfile,
    onboardingState,
    trpc,
  ]);

  useEffect(() => {
    window.addEventListener('dappListUpdated', refetchDapps);
    return () => {
      window.removeEventListener('dappListUpdated', refetchDapps);
    };
  }, [refetchDapps]);

  return createElement(
    SidebarDataContext.Provider,
    {
      value: {
        data,
        isLoading,
        refetchDapps,
        removeDapp,
      },
    },
    children,
  );
};

export const useSidebarData = (): SidebarDataContextValue => {
  const context = use(SidebarDataContext);

  if (!context) {
    throw new Error('useSidebarData must be used within SidebarDataProvider');
  }

  return context;
};
